/**
 * P-02's form state, as data rather than as closures.
 *
 * It lives outside the component for one reason: every one of these updates has
 * to be a function of the PREVIOUS state, not of a snapshot captured when the
 * handler was created.
 *
 * `setForm({ ...form, city: value })` reads correct and is not. `form` there is
 * the value from the render that made the handler, so when two change events
 * land in the same React batch — which is exactly what a browser autofilling an
 * address does, and what a value picked from a dropdown can do alongside the
 * field it was typed in — the second update is built on the state the first one
 * had already replaced, and every field the first one wrote is dropped back to
 * what it was. On a nine-field public form that reads as "the form cleared
 * itself". Nothing is reinitialised and nothing remounts; the typed values are
 * simply overwritten by a stale copy.
 *
 * Expressed here as pure functions, the component can only call them through
 * `setForm((f) => …)`, where React hands in the current state. The class of bug
 * is gone rather than fixed, and the rules are testable without a browser.
 */

export interface ApplyForm {
  portalCode: string;
  businessName: string;
  firstName: string;
  lastName: string;
  contactEmail: string;
  phone: string;
  countryCode: string;
  city: string;
  postalCode: string;
  registrationId: string;
  businessType: string;
  buyingGroup: string;
  note: string;
}

export const emptyApplyForm: ApplyForm = {
  portalCode: '', businessName: '', firstName: '', lastName: '', contactEmail: '',
  phone: '', countryCode: '', city: '', postalCode: '', registrationId: '',
  businessType: '', buyingGroup: '', note: '',
};

/** ON-02 — one trade reference row as the applicant builds it. */
export interface ApplyRef {
  company: string;
  contactName: string;
  email: string;
  phone: string;
  relationship: string;
}

export const emptyApplyRef: ApplyRef = {
  company: '', contactName: '', email: '', phone: '', relationship: '',
};

/** One field, changed. Every other field is carried through untouched. */
export function withField<K extends keyof ApplyForm>(
  form: ApplyForm, key: K, value: string,
): ApplyForm {
  return { ...form, [key]: value };
}

/**
 * ON-03 — the country, changed.
 *
 * Deliberately the same shape as any other field. Choosing a country RELABELS
 * two fields from the country pack — "Postal code" becomes "PO Box", "Business
 * registration" becomes "TRN" — and that is a change of label, not of content:
 * nothing the applicant has typed is theirs to discard. Not even the
 * registration ID, which is the one value the new country might invalidate: it
 * is revalidated against the new pack on the next blur and the applicant is
 * told, which is strictly better than silently emptying the box they just
 * filled in.
 */
export function withCountry(form: ApplyForm, countryCode: string): ApplyForm {
  return withField(form, 'countryCode', countryCode);
}

/** The portal, changed. Same rule, same reason. */
export function withPortal(form: ApplyForm, portalCode: string): ApplyForm {
  return withField(form, 'portalCode', portalCode);
}

/** The first portal on offer, unless the applicant has already chosen one. */
export function withDefaultPortal(form: ApplyForm, firstOnOffer: string | undefined): ApplyForm {
  return form.portalCode ? form : withField(form, 'portalCode', firstOnOffer ?? '');
}

/** One field of one reference row, changed. */
export function withRefField<K extends keyof ApplyRef>(
  refs: readonly ApplyRef[], index: number, key: K, value: string,
): ApplyRef[] {
  return refs.map((r, i) => (i === index ? { ...r, [key]: value } : r));
}

export function withRefAdded(refs: readonly ApplyRef[]): ApplyRef[] {
  return [...refs, { ...emptyApplyRef }];
}

export function withRefRemoved(refs: readonly ApplyRef[], index: number): ApplyRef[] {
  return refs.filter((_, i) => i !== index);
}
